import axios from 'axios';

class DashboardApiService {
  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'https://codelens.epoch.co/openshield/',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for Cognito token authentication
    this.api.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('cognito_token');
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          // If no token, reject the request
          return Promise.reject(new Error('No authentication token available'));
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('cognito_token');
          localStorage.removeItem('user_info');
          window.location.href = 'https://codelens.epoch.co/';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch all agents from the compliance backend
   * @returns {Promise<Array>} Array of agent objects
   */
  async getAllAgents() {
    try {
      const response = await this.api.get('agents/');
      return response.data;
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw new Error(`Failed to fetch agents: ${error.message}`);
    }
  }

  /**
   * Fetch a single agent by ID from the compliance backend
   * @param {string} agentId - The agent ID to fetch
   * @returns {Promise<Object>} Agent object
   */
  async getAgentById(agentId) {
    try {
      const response = await this.api.get(`/agents/${agentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent:', error);
      throw new Error(`Failed to fetch agent ${agentId}: ${error.message}`);
    }
  }

  /**
   * Get agent statistics from the agents data
   * @param {Array} agents - Array of agent objects
   * @returns {Object} Statistics object
   */
  getAgentStatistics(agents) {
    const totalAgents = agents.length;
    const activeAgents = agents.filter(agent => agent.status === 'active').length;
    const inactiveAgents = agents.filter(agent => agent.status === 'disconnected').length;

    return {
      totalAgents,
      activeAgents,
      inactiveAgents,
    };
  }

  /**
   * Get pie chart data for agent status distribution
   * @param {Array} agents - Array of agent objects
   * @returns {Array} Pie chart data array
   */
  getPieChartData(agents) {
    const activeAgents = agents.filter(agent => agent.status === 'active').length;
    const inactiveAgents = agents.filter(agent => agent.status === 'disconnected').length;

    return [
      {
        name: 'Active',
        value: activeAgents,
        color: '#10b981',
        fill: '#10b981'
      },
      {
        name: 'Inactive',
        value: inactiveAgents,
        color: '#ef4444',
        fill: '#ef4444'
      }
    ];
  }
}

// Create and export a singleton instance
const dashboardApiService = new DashboardApiService();
export default dashboardApiService;
